import { useRef, useEffect, useLayoutEffect } from 'react';
import { motion } from 'framer-motion';
import { buildStreamlinePath, FLOW_PRESETS, lineOpacity } from './flowField';

export default function FlowFieldBackground({
  gradientId = 'flow-grad',
  preset = 'hero',
  fadeIn = true,
  uniformStroke = false,
  strokeOpacity = 0.72,
  style,
}) {
  const cfg = FLOW_PRESETS[preset] ?? FLOW_PRESETS.hero;
  const pathRefs = useRef([]);
  const gradientRef = useRef(null);
  const presetRef = useRef(preset);
  presetRef.current = preset;

  useLayoutEffect(() => {
    pathRefs.current.forEach((el, i) => {
      if (el) el.setAttribute('d', buildStreamlinePath(i, 0, presetRef.current));
    });
  }, [preset, cfg.count]);

  useEffect(() => {
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      pathRefs.current.forEach((el, i) => el && el.setAttribute('d', buildStreamlinePath(i, 0, presetRef.current)));
      return undefined;
    }

    let raf = 0;
    let t = 0;
    let last = performance.now();

    const tick = (now) => {
      if (!document.hidden) {
        const dt = Math.min((now - last) / 1000, 0.05);
        last = now;
        t += dt;

        for (let i = 0; i < cfg.count; i += 1) {
          const el = pathRefs.current[i];
          if (el) el.setAttribute('d', buildStreamlinePath(i, t, presetRef.current));
        }

        if (!uniformStroke) {
          const grad = gradientRef.current;
          if (grad) {
            const GRAD_TILE = 480;
            grad.setAttribute('gradientTransform', `translate(${-((t * 24) % GRAD_TILE)}, 0)`);
          }
        }
      } else {
        last = now;
      }

      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [preset, cfg.count, uniformStroke]);

  const Wrapper = fadeIn ? motion.div : 'div';
  const wrapperProps = fadeIn
    ? {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        transition: { duration: 1.6, ease: 'easeOut' },
      }
    : {};

  return (
    <Wrapper
      aria-hidden="true"
      {...wrapperProps}
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        overflow: 'hidden',
        zIndex: 0,
        ...style,
      }}
    >
      <svg
        viewBox={`0 0 ${cfg.flowW} ${cfg.flowH}`}
        preserveAspectRatio="xMidYMid slice"
        style={{ width: '100%', height: '100%', display: 'block' }}
        fill="none"
      >
        <defs>
          {!uniformStroke && (
            <linearGradient
              ref={gradientRef}
              id={gradientId}
              gradientUnits="userSpaceOnUse"
              spreadMethod="repeat"
              x1="0"
              y1={cfg.flowH / 2}
              x2="480"
              y2={cfg.flowH / 2}
            >
              <stop offset="0%" stopColor="var(--hero-flow-strong)" stopOpacity="0" />
              <stop offset="14%" stopColor="var(--hero-flow-strong)" stopOpacity="1" />
              <stop offset="48%" stopColor="var(--hero-flow-strong)" stopOpacity="0.9" />
              <stop offset="74%" stopColor="var(--hero-flow-soft)" stopOpacity="0.8" />
              <stop offset="100%" stopColor="var(--hero-flow-soft)" stopOpacity="0.1" />
            </linearGradient>
          )}
        </defs>
        {Array.from({ length: cfg.count }).map((_, i) => (
          <path
            key={i}
            ref={(el) => { pathRefs.current[i] = el; }}
            stroke={uniformStroke ? 'var(--hero-flow-strong)' : `url(#${gradientId})`}
            strokeWidth="0.85"
            strokeLinecap="round"
            shapeRendering="geometricPrecision"
            opacity={uniformStroke ? strokeOpacity : lineOpacity(i, preset)}
          />
        ))}
      </svg>
    </Wrapper>
  );
}
